import React from "react";
import { auth, db } from "../firebase-client";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import {
  getCollectionDoc,
  getCollectionData,
  removeDoc,
  writeCollectionDoc,
  updateCollectionDoc,
  getDb,
} from "./firestore-api";
import { collection, getDocs, getDoc, doc, query, limit, orderBy, startAfter, where } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";
import type {
  Buyer,
  Farmer,
  Worker,
  Project,
  PurchaseEntry,
  ProductionEntry,
  PurchaseReceipt,
  Category,
  Expense,
  SalesReceipt,
  SalesEntry,
  Income,
  Stock,
  UserProfile,
  UserSettings,
} from "./types";

// Helper to get auth (for compatibility with original code)
const getAuth = () => {
  if (!auth) {
    throw new Error("Firebase auth not initialized");
  }
  return auth;
};

async function callGenerateInvoice(userID: any, projectID: any) {
  const functions = getFunctions();
  const generateInvoice = httpsCallable(functions, "generateInvoice");

  try {
    const response = await generateInvoice({ userID, projectID });
    console.log("Invoice URL:", (response.data as any)?.url);
  } catch (error) {
    console.error("Error calling cloud function:", error);
  }
}

// paths
const basePath = (uid: string) => `users/${uid}`;

export const useCategories = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/categories`;
  const client = useQueryClient();

  return useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => {
      const rows = await getCollectionData(path) as Category[];
      rows.forEach((row) => client.setQueryData(["categories", row.id], row));
      return rows;
    },
  });
};

export const useAddCategory = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/categories`;
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: any }) => writeCollectionDoc(path, data),
    onSuccess: () => client.invalidateQueries({ queryKey: ["categories"] }),
  });
};

export const useEditCategory = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/categories`;
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ data, categoryID }: { data: any; categoryID: string }) => updateCollectionDoc(path, data, categoryID),
    onSuccess: () => client.invalidateQueries({ queryKey: ["categories"] }),
  });
};

export const useDeleteCategory = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/categories`;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (categoryID: string) => removeDoc(path, categoryID),
    onSuccess: () => client.invalidateQueries({ queryKey: ["categories"] }),
  });
};

// ************************************************* Expenses ****************************************************
// Add Expense
export const useAddExpense = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/expenses`;
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: any) =>
      writeCollectionDoc(path, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      }),
    onSuccess: (_res, vars: any) => {
      client.invalidateQueries({ queryKey: ["expenses"] });
      if (vars?.data?.projectId) {
        client.invalidateQueries({ queryKey: ["expenses", { projectId: vars.data.projectId }] });
      }
      client.invalidateQueries({ queryKey: ["summaries"] });
    },
  });
};

// Edit Expense
export const useEditExpense = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/expenses`;
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ data, expenseID }: { data: any; expenseID: string }) => updateCollectionDoc(path, { ...data, updatedAt: new Date() }, expenseID),
    onSuccess: (_res, vars) => {
      client.invalidateQueries({ queryKey: ["expenses"] });
      if (vars?.data?.projectId) {
        client.invalidateQueries({ queryKey: ["expenses", { projectId: vars.data.projectId }] });
      }
      client.invalidateQueries({ queryKey: ["summaries"] });
    },
  });
};

// List Expenses with optional filters
export const useExpenses = ({
  projectId,
  categoryId,
  accountId,
  from,
  to,
  pageSize = 100
}: {
  projectId?: string;
  categoryId?: string;
  accountId?: string;
  from?: string;
  to?: string;
  pageSize?: number;
}) => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const colRef = collection(getDb(), `${basePath(userID)}/expenses`);

  return useInfiniteQuery({
    queryKey: ["expenses", { projectId, categoryId, accountId, from, to }],
    queryFn: async ({ pageParam = null }) => {
      let q = query(colRef, orderBy("date", "desc"), limit(pageSize));
      if (projectId) q = query(q, where("projectId", "==", projectId));
      if (categoryId) q = query(q, where("categoryId", "==", categoryId));
      if (accountId) q = query(q, where("accountId", "==", accountId));
      if (from) q = query(q, where("date", ">=", from));
      if (to) q = query(q, where("date", "<=", to));
      if (pageParam) q = query(q, startAfter(pageParam));

      const snap = await getDocs(q);
      const lastVisible = snap.docs[snap.docs.length - 1];
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      return { rows, lastVisible };
    },
    initialPageParam: null,
    getNextPageParam: (last: any) => last?.lastVisible || undefined,
  });
};

export const useDeleteExpense = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/expenses`;
  const client = useQueryClient();

  return useMutation({
    mutationFn: (expenseID: string) => removeDoc(path, expenseID),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["expenses"] });
      client.invalidateQueries({ queryKey: ["summaries"] });
    },
  });
};
// ******************************************************************************

export const useGenerateInvoice = () => {
  const client = useQueryClient();
  const functions = getFunctions();
  const generateInvoiceFn = httpsCallable(functions, "generateInvoice");
  const userID = auth?.currentUser?.uid;

  return useMutation({
    mutationFn: ({ projectID }: { projectID: string }) => {
      console.log("before calling API");
      return generateInvoiceFn({ userID, projectID });
    },
    onSuccess: (_, { projectID }) => {
      client.invalidateQueries({ queryKey: ["purchasereceipts", projectID] });
    },
  });
};

export const useGenerateSalesInvoice = () => {
  const client = useQueryClient();
  const functions = getFunctions();
  const generateSalesInvoiceFn = httpsCallable(functions, "generateSalesInvoice");
  const userID = auth?.currentUser?.uid;

  return useMutation({
    mutationFn: ({ receiptID }: { receiptID: string }) =>
      generateSalesInvoiceFn({ userID, receiptID }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["salesreceipts"] });
    },
  });
};

// Fetch a single farmer's data by ID
export const useFarmer = (farmerID: string) => {
  const farmerPath = `users/${auth?.currentUser?.uid}/farmers`;

  return useQuery<Farmer>({
    queryKey: ["farmers", farmerID],
    queryFn: () => {
      console.log(`Fetching farmer: ${farmerID}`);
      return getCollectionDoc(farmerPath, farmerID) as Promise<Farmer>;
    },
    enabled: !!farmerID, // Only fetch if farmerID exists
  });
};

// Mutation to add a new farmer
export const useAddFarmer = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/farmers`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding farmer:", data);
      return writeCollectionDoc(farmerPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["farmers"] });
    },
  });
};

// Mutation to edit an existing farmer
export const useEditFarmer = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/farmers`;

  return useMutation({
    mutationFn: ({ data, farmerID }: { data: any; farmerID: string }) => {
      console.info("Updating farmer:", data);
      return updateCollectionDoc(farmerPath, data, farmerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["farmers"] });
    },
  });
};

// Fetch all farmers
export const useFarmers = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/farmers`;

  return useQuery<Farmer[]>({
    queryKey: ["farmers"],
    queryFn: async () => {
      console.log("Fetching all farmers");
      const farmers = await getCollectionData(farmerPath) as Farmer[];

      // Preload individual farmer data into cache
      farmers.forEach((farmer) => {
        client.setQueryData(["farmers", farmer.id], farmer);
      });

      return farmers;
    },
  });
};

const FARMERS_PER_PAGE = 100; // Number of farmers per page

export const usePaginationFarmer = (search = "") => {
  const client = useQueryClient();

  const farmerPath = collection(getDb(), `users/${auth?.currentUser?.uid}/farmers`);

  return useInfiniteQuery({
    queryKey: ["farmers", search], // Object-based syntax with search key

    queryFn: async ({ pageParam = null }) => {
      console.log("Fetching farmers...");
      try {
        // Build the Firestore query
        let farmerQuery = query(
          farmerPath,
          orderBy("last_name"), // Replace with the correct field
          limit(FARMERS_PER_PAGE)
        );

        // If we have a pageParam (the last document from the previous page), use it for `startAfter`
        if (pageParam) {
          farmerQuery = query(farmerQuery, startAfter(pageParam));
        }

        // Fetch the farmers from Firestore
        const querySnapshot = await getDocs(farmerQuery);

        // Get the last document for the next page's starting point
        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

        // Map the Firestore documents to an array of farmer objects
        const farmers = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Optionally cache individual farmers (optimization)
        farmers.forEach((farmer) => {
          client.setQueryData(["farmers", farmer.id], farmer);
        });

        return { farmers, lastVisible };
      } catch (error) {
        console.log("farmers error", error);
      }
    },

    initialPageParam: null,
    getNextPageParam: (lastPage: any) => (lastPage?.lastVisible ? lastPage.lastVisible : undefined), // Pass last document to fetch next page
  });
};

// Mutation to delete a farmer
export const useDeleteFarmer = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/farmers`;

  return useMutation({
    mutationFn: (farmerID: string) => {
      console.info("Deleting farmer:", farmerID);
      return removeDoc(farmerPath, farmerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["farmers"] });
    },
  });
};

// ************************************************* Workers ****************************************************
// Fetch a single worker's data by ID
export const useWorker = (workerID: string) => {
  const workerPath = `users/${auth?.currentUser?.uid}/workers`;

  return useQuery<Worker>({
    queryKey: ["workers", workerID],
    queryFn: () => {
      console.log(`Fetching worker: ${workerID}`);
      return getCollectionDoc(workerPath, workerID) as Promise<Worker>;
    },
    enabled: !!workerID,
  });
};

// Mutation to add a new worker
export const useAddWorker = () => {
  const client = useQueryClient();
  const workerPath = `users/${auth?.currentUser?.uid}/workers`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding worker:", data);
      return writeCollectionDoc(workerPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["workers"] });
    },
  });
};

// Mutation to edit an existing worker
export const useEditWorker = () => {
  const client = useQueryClient();
  const workerPath = `users/${auth?.currentUser?.uid}/workers`;

  return useMutation({
    mutationFn: ({ data, workerID }: { data: any; workerID: string }) => {
      console.info("Updating worker:", data);
      return updateCollectionDoc(workerPath, data, workerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["workers"] });
    },
  });
};

// Fetch all workers
export const useWorkers = () => {
  const client = useQueryClient();
  const workerPath = `users/${auth?.currentUser?.uid}/workers`;

  return useQuery<Worker[]>({
    queryKey: ["workers"],
    queryFn: async () => {
      console.log("Fetching all workers");
      const workers = await getCollectionData(workerPath) as Worker[];

      // Preload individual worker data into cache
      workers.forEach((worker) => {
        client.setQueryData(["workers", worker.id], worker);
      });

      return workers;
    },
  });
};

// Mutation to delete a worker
export const useDeleteWorker = () => {
  const client = useQueryClient();
  const workerPath = `users/${auth?.currentUser?.uid}/workers`;

  return useMutation({
    mutationFn: (workerID: string) => {
      console.info("Deleting worker:", workerID);
      return removeDoc(workerPath, workerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["workers"] });
    },
  });
};

// ************************************************* Buyers ****************************************************
// Fetch a single buyer's data by ID
export const useBuyer = (buyerID: string) => {
  const buyerPath = `users/${auth?.currentUser?.uid}/buyers`;

  return useQuery<Buyer>({
    queryKey: ["buyers", buyerID],
    queryFn: () => {
      console.log(`Fetching buyer: ${buyerID}`);
      return getCollectionDoc(buyerPath, buyerID) as Promise<Buyer>;
    },
    enabled: !!buyerID,
  });
};

// Mutation to add a new buyer
export const useAddBuyer = () => {
  const client = useQueryClient();
  const buyerPath = `users/${auth?.currentUser?.uid}/buyers`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding buyer:", data);
      return writeCollectionDoc(buyerPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["buyers"] });
    },
  });
};

// Mutation to edit an existing buyer
export const useEditBuyer = () => {
  const client = useQueryClient();
  const buyerPath = `users/${auth?.currentUser?.uid}/buyers`;

  return useMutation({
    mutationFn: ({ data, buyerID }: { data: any; buyerID: string }) => {
      console.info("Updating buyer:", data);
      return updateCollectionDoc(buyerPath, data, buyerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["buyers"] });
    },
  });
};

// Fetch all buyers
export const useBuyers = () => {
  const client = useQueryClient();
  const buyerPath = `users/${auth?.currentUser?.uid}/buyers`;

  return useQuery<Buyer[]>({
    queryKey: ["buyers"],
    queryFn: async () => {
      console.log("Fetching all buyers");
      const buyers = await getCollectionData(buyerPath) as Buyer[];

      // Preload individual buyer data into cache
      buyers.forEach((buyer) => {
        client.setQueryData(["buyers", buyer.id], buyer);
      });

      return buyers;
    },
  });
};

// Mutation to delete a buyer
export const useDeleteBuyer = () => {
  const client = useQueryClient();
  const buyerPath = `users/${auth?.currentUser?.uid}/buyers`;

  return useMutation({
    mutationFn: (buyerID: string) => {
      console.info("Deleting buyer:", buyerID);
      return removeDoc(buyerPath, buyerID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["buyers"] });
    },
  });
};

///  *************************project queries *************************************************************
// Mutation to add a new project
export const useAddProject = () => {
  const client = useQueryClient();
  const projectPath = `users/${auth?.currentUser?.uid}/projects`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding Project:", data);
      return writeCollectionDoc(projectPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

// Fetch all projects
export const useProjects = () => {
  const client = useQueryClient();
  const userID = auth?.currentUser?.uid;

  return useQuery<Project[]>({
    queryKey: ["projects", userID],
    queryFn: async () => {
      if (!userID) {
        throw new Error("User not authenticated");
      }
      const projectPath = `users/${userID}/projects`;
      console.log("Fetching all projects for user:", userID);
      const projects = await getCollectionData(projectPath) as Project[];

      // Preload individual farmer data into cache
      projects.forEach((project) => {
        client.setQueryData(["projects", project.id], project);
      });
      console.log("projects", projects);
      return projects;
    },
    enabled: !!userID, // Only run query if user is authenticated
  });
};

// Fetch a single farmer's data by ID
export const useProject = (projectID: string) => {
  const farmerPath = `users/${auth?.currentUser?.uid}/projects`;

  return useQuery<Project>({
    queryKey: ["projects", projectID],
    queryFn: () => {
      console.log(`Fetching farmer: ${projectID}`);
      return getCollectionDoc(farmerPath, projectID) as Promise<Project>;
    },
    enabled: !!projectID, // Only fetch if projectID exists
  });
};

// Mutation to edit an existing project
export const useEditProject = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/projects`;

  return useMutation({
    mutationFn: ({ data, projectID }: { data: any; projectID: string }) => {
      console.info("Updating project:", data);
      return updateCollectionDoc(farmerPath, data, projectID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

// Mutation to delete a project
export const useDeleteProject = () => {
  const client = useQueryClient();
  const farmerPath = `users/${auth?.currentUser?.uid}/projects`;

  return useMutation({
    mutationFn: (projectID: string) => {
      console.info("Deleting farmer:", projectID);
      return removeDoc(farmerPath, projectID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

//  ************************* end projects queries *************************************************************
export const useAddPurchaseEntry = (projectID: string) => {
  const client = useQueryClient();
  const uid = auth?.currentUser?.uid;
  const purchasePath = `users/${uid}/projects/${projectID}/purchaseentries`;
  const projectPath = `users/${uid}/projects`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding Purchase Entry:", data);
      return writeCollectionDoc(purchasePath, data);
    },
    onSuccess: async () => {
      // Auto-advance project status "new" → "inprogress" on first purchase entry
      const project = client.getQueryData<any>(["projects", projectID]);
      if (project?.project_status === "new") {
        await updateCollectionDoc(projectPath, { project_status: "inprogress" }, projectID);
        client.invalidateQueries({ queryKey: ["projects"] });
        client.invalidateQueries({ queryKey: ["projects", projectID] });
      }
      client.invalidateQueries({ queryKey: ["purchaseentries", projectID] });
    },
  });
};

export const usePurchaseEntries = (projectID: string) => {
  const client = useQueryClient();
  const purchasePath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchaseentries`;

  return useQuery<PurchaseEntry[]>({
    queryKey: ["purchaseentries", projectID],
    queryFn: async () => {
      console.log(`Fetching all Purchase Entries for project: ${projectID}`);
      const purchaseentries = await getCollectionData(purchasePath) as PurchaseEntry[];

      // Cache individual purchase entries
      purchaseentries.forEach((entry) => {
        client.setQueryData(["purchaseentries", projectID, entry.id], entry);
      });

      return purchaseentries;
    },
    enabled: !!projectID, // Only fetch if projectID is available
  });
};

export const usePurchaseEntry = (projectID: string, entryID: string) => {
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchaseentries`;

  return useQuery<PurchaseEntry>({
    queryKey: ["purchaseentries", projectID, entryID],
    queryFn: () => {
      console.log(`Fetching Purchase Entry: ${entryID}`);
      return getCollectionDoc(entryPath, entryID) as Promise<PurchaseEntry>;
    },
    enabled: !!entryID, // Only fetch if entryID exists
  });
};
export const useEditPurchaseEntry = (projectID: string) => {
  const client = useQueryClient();
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchaseentries`;

  return useMutation({
    mutationFn: ({ data, entryID }: { data: any; entryID: string }) => {
      console.info("Updating Purchase Entry:", data);
      return updateCollectionDoc(entryPath, data, entryID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["purchaseentries", projectID] });
    },
  });
};

export const useDeletePurchaseEntry = (projectID: string) => {
  const client = useQueryClient();
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchaseentries`;

  return useMutation({
    mutationFn: (entryID: string) => {
      console.info("Deleting Purchase Entry:", entryID);
      return removeDoc(entryPath, entryID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["purchaseentries", projectID] });
    },
  });
};
// *******************************************************************************************************************

// 1. Add Production Entry Hook
export const useAddProductionEntry = (projectID: string) => {
  const client = useQueryClient();
  const productionPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/productionentries`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding Production Entry:", data);
      return writeCollectionDoc(productionPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["productionentries", projectID] });
    },
  });
};

// 2. Fetch All Production Entries Hook
export const useProductionEntries = (projectID: string) => {
  const client = useQueryClient();
  const productionPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/productionentries`;

  return useQuery<ProductionEntry[]>({
    queryKey: ["productionentries", projectID],
    queryFn: async () => {
      console.log(`Fetching all Production Entries for project: ${projectID}`);
      const productionEntries = await getCollectionData(productionPath) as ProductionEntry[];

      // Cache individual production entries
      productionEntries.forEach((entry) => {
        client.setQueryData(["productionentries", projectID, entry.id], entry);
      });

      return productionEntries;
    },
    enabled: !!projectID, // Only fetch if projectID is available
  });
};

// 3. Fetch a Single Production Entry Hook
export const useProductionEntry = (projectID: string, entryID: string) => {
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/productionentries`;

  return useQuery<ProductionEntry>({
    queryKey: ["productionentries", projectID, entryID],
    queryFn: () => {
      console.log(`Fetching Production Entry: ${entryID}`);
      return getCollectionDoc(entryPath, entryID) as Promise<ProductionEntry>;
    },
    enabled: !!entryID, // Only fetch if entryID exists
  });
};

// 4. Edit Production Entry Hook
export const useEditProductionEntry = (projectID: string) => {
  const client = useQueryClient();
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/productionentries`;

  return useMutation({
    mutationFn: ({ data, entryID }: { data: any; entryID: string }) => {
      console.info("Updating Production Entry:", data);
      return updateCollectionDoc(entryPath, data, entryID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["productionentries", projectID] });
    },
  });
};

// 5. Delete Production Entry Hook
export const useDeleteProductionEntry = (projectID: string) => {
  const client = useQueryClient();
  const entryPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/productionentries`;

  return useMutation({
    mutationFn: (entryID: string) => {
      console.info("Deleting Production Entry:", entryID);
      return removeDoc(entryPath, entryID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["productionentries", projectID] });
    },
  });
};

//************************************************************************** */
export const useAddPurchaseReceipt = (projectID: string) => {
  const client = useQueryClient();
  const receiptPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchasereceipts`;

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      console.info("Adding Purchase Receipt:", data);
      return writeCollectionDoc(receiptPath, data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["purchasereceipts", projectID] });
    },
  });
};

export const usePurchaseReceipts = (projectID: string) => {
  const client = useQueryClient();
  const receiptPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchasereceipts`;

  return useQuery<PurchaseReceipt[]>({
    queryKey: ["purchasereceipts", projectID],
    queryFn: async () => {
      console.log(`Fetching all Purchase Receipts for project: ${projectID}`);
      const purchasereceipts = await getCollectionData(receiptPath) as PurchaseReceipt[];

      purchasereceipts.forEach((receipt) => {
        client.setQueryData(["purchasereceipts", projectID, receipt.id], receipt);
      });

      return purchasereceipts;
    },
    enabled: !!projectID,
  });
};

export const usePurchaseReceipt = (projectID: string, receiptID: string) => {
  const receiptPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchasereceipts`;

  return useQuery<PurchaseReceipt>({
    queryKey: ["purchasereceipts", projectID, receiptID],
    queryFn: () => {
      console.log(`Fetching Purchase Receipt: ${receiptID}`);
      return getCollectionDoc(receiptPath, receiptID) as Promise<PurchaseReceipt>;
    },
    enabled: !!receiptID,
  });
};

export const useEditPurchaseReceipt = (projectID: string) => {
  const client = useQueryClient();
  const receiptPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchasereceipts`;

  return useMutation({
    mutationFn: ({ data, receiptID }: { data: any; receiptID: string }) => {
      console.info("Updating Purchase Receipt:", data);
      return updateCollectionDoc(receiptPath, data, receiptID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["purchasereceipts", projectID] });
    },
  });
};

export const useDeletePurchaseReceipt = (projectID: string) => {
  const client = useQueryClient();
  const receiptPath = `users/${auth?.currentUser?.uid}/projects/${projectID}/purchasereceipts`;

  return useMutation({
    mutationFn: (receiptID: string) => {
      console.info("Deleting Purchase Receipt:", receiptID);
      return removeDoc(receiptPath, receiptID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["purchasereceipts", projectID] });
    },
  });
};

//************************************************************************** */
// STOCK/INVENTORY MANAGEMENT
//************************************************************************** */

// Get current stock/inventory (pre-calculated by Cloud Functions)
export const useCurrentStock = () => {
  const userID = auth?.currentUser?.uid;

  return useQuery({
    queryKey: ["currentStock", userID],
    queryFn: async () => {
      console.log("Fetching pre-calculated stock for user:", userID);

      // Fetch pre-calculated stock from userAppLevelData
      const statsPath = `users/${userID}/userAppLevelData/stats`;
      const statsDoc = await getDoc(doc(getDb(), statsPath));

      if (!statsDoc.exists()) {
        console.log("No stock data found, returning empty array");
        return [];
      }

      const statsData = statsDoc.data();
      const totalStock = statsData?.totalStock || [];

      // Transform the data to match the Stock type interface
      // Firestore format: { type: "5", quantity: 325 }
      // Expected format: { production_type: "5", total_quantity: 325, total_weight: 0 }
      return totalStock.map((item: any) => ({
        production_type: item.type || '',
        total_quantity: item.quantity || 0,
        total_weight: 0, // Not stored in Firestore, default to 0
      }));
    },
    enabled: !!userID,
  });
};

// Manually trigger stock recalculation (calls Cloud Function)
export const useRecalculateStock = () => {
  const client = useQueryClient();
  const functions = getFunctions();
  const recalculateStockFn = httpsCallable(functions, "recalculateStock");

  return useMutation({
    mutationFn: async () => {
      console.log("Triggering manual stock recalculation");
      return recalculateStockFn();
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

//************************************************************************** */
// SALES RECEIPTS MANAGEMENT
//************************************************************************** */

// Add Sales Receipt
export const useAddSalesReceipt = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesreceipts`;

  return useMutation({
    mutationFn: ({ data }: any) => {
      console.info("Adding Sales Receipt:", data);
      return writeCollectionDoc(path, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["salesreceipts"] });
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

// Get all Sales Receipts
export const useSalesReceipts = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const colRef = collection(getDb(), `${basePath(userID)}/salesreceipts`);

  return useQuery<SalesReceipt[]>({
    queryKey: ["salesreceipts"],
    queryFn: async () => {
      const q = query(colRef, orderBy("date", "desc"));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as SalesReceipt[];
    },
  });
};

// Get single Sales Receipt
export const useSalesReceipt = (receiptId: string) => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesreceipts`;

  return useQuery<SalesReceipt>({
    queryKey: ["salesreceipt", receiptId],
    queryFn: () => {
      console.log(`Fetching Sales Receipt: ${receiptId}`);
      return getCollectionDoc(path, receiptId) as Promise<SalesReceipt>;
    },
    enabled: !!receiptId,
  });
};

// Edit Sales Receipt
export const useEditSalesReceipt = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesreceipts`;

  return useMutation({
    mutationFn: ({ data, receiptId }: any) => {
      console.info("Updating Sales Receipt:", data);
      return updateCollectionDoc(path, { ...data, updatedAt: new Date() }, receiptId);
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: ["salesreceipts"] });
      client.invalidateQueries({ queryKey: ["salesreceipt", variables.receiptId] });
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

// Delete Sales Receipt
export const useDeleteSalesReceipt = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesreceipts`;

  return useMutation({
    mutationFn: (receiptId: string) => {
      console.info("Deleting Sales Receipt:", receiptId);
      return removeDoc(path, receiptId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["salesreceipts"] });
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

//************************************************************************** */
// SALES ENTRIES (items in sales receipts that reduce stock)
//************************************************************************** */

// Add Sales Entry (reduces stock)
export const useAddSalesEntry = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesentries`;

  return useMutation({
    mutationFn: ({ data }: any) => {
      console.info("Adding Sales Entry:", data);
      return writeCollectionDoc(path, {
        ...data,
        createdAt: new Date(),
      });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["salesentries"] });
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

// Get Sales Entries for a receipt
export const useSalesEntries = (receiptId: string) => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const colRef = collection(getDb(), `${basePath(userID)}/salesentries`);

  return useQuery<SalesEntry[]>({
    queryKey: ["salesentries", receiptId],
    queryFn: async () => {
      const q = query(colRef, where("receiptId", "==", receiptId));
      const querySnapshot = await getDocs(q);
      const entries = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })) as SalesEntry[];
      console.log(`Found ${entries.length} sales entries for receipt ${receiptId}:`, entries);
      return entries;
    },
    enabled: !!receiptId,
  });
};

// Delete Sales Entry
export const useDeleteSalesEntry = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/salesentries`;

  return useMutation({
    mutationFn: ({ entryID }: any) => {
      console.info("Deleting Sales Entry:", entryID);
      return removeDoc(path, entryID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["salesentries"] });
      client.invalidateQueries({ queryKey: ["currentStock"] });
    },
  });
};

//************************************************************************** */
// INCOME ENTRIES
//************************************************************************** */

// Get all Income Entries
export const useIncomes = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const colRef = collection(getDb(), `${basePath(userID)}/incomes`);

  return useQuery<Income[]>({
    queryKey: ["incomes"],
    queryFn: async () => {
      const q = query(colRef, orderBy("date", "desc"));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Income[];
    },
  });
};

// Get single Income Entry
export const useIncome = (incomeId: string) => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/incomes`;

  return useQuery<Income>({
    queryKey: ["income", incomeId],
    queryFn: () => getCollectionDoc(path, incomeId) as Promise<Income>,
    enabled: !!incomeId,
  });
};

// Add Income Entry
export const useAddIncome = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/incomes`;

  return useMutation({
    mutationFn: ({ data }: any) => {
      console.info("Adding Income Entry:", data);
      return writeCollectionDoc(path, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["incomes"] });
    },
  });
};

// Edit Income Entry
export const useEditIncome = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/incomes`;

  return useMutation({
    mutationFn: ({ data, incomeId }: any) => {
      console.info("Updating Income Entry:", data);
      return updateCollectionDoc(path, { ...data, updatedAt: new Date() }, incomeId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["incomes"] });
    },
  });
};

// Delete Income Entry
export const useDeleteIncome = () => {
  const client = useQueryClient();
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `${basePath(userID)}/incomes`;

  return useMutation({
    mutationFn: ({ incomeId }: any) => {
      console.info("Deleting Income Entry:", incomeId);
      return removeDoc(path, incomeId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["incomes"] });
    },
  });
};

//************************************************************************** */

// ************************************************* User Profile ****************************************************
// Fetch user profile
export const useProfile = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `users`;

  return useQuery<UserProfile>({
    queryKey: ["profile", userID],
    queryFn: () => {
      console.log(`Fetching profile for user: ${userID}`);
      return getCollectionDoc(path, userID) as Promise<UserProfile>;
    },
    enabled: !!userID,
  });
};

// Update user profile
export const useUpdateProfile = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `users`;
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: any) => {
      console.info("Updating profile:", data);
      return updateCollectionDoc(path, { ...data, updatedAt: new Date() }, userID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["profile", userID] });
    },
  });
};

// Update user preferences
export const useUpdatePreferences = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const path = `users`;
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ preferences }: any) => {
      console.info("Updating preferences:", preferences);
      return updateCollectionDoc(path, { preferences, updatedAt: new Date() }, userID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["profile", userID] });
    },
  });
};

const DEFAULT_SETTINGS: UserSettings = {
  purchase_rate_unit: 'per_kg',
  weight_deduction_rate: 1,
  firm_name: '',
  currency: 'INR',
  theme: 'light',
  language: 'en',
};

// Fetch user settings (read from the same users/{uid} doc)
export const useSettings = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;

  return useQuery<UserSettings>({
    queryKey: ["profile", userID],
    queryFn: () => getCollectionDoc("users", userID) as Promise<any>,
    enabled: !!userID,
    select: (data: any) => data?.settings ?? DEFAULT_SETTINGS,
  });
};

// Save user settings
export const useUpdateSettings = () => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ settings }: { settings: UserSettings }) => {
      console.info("Updating settings:", settings);
      return updateCollectionDoc("users", { settings, updatedAt: new Date() }, userID);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["profile", userID] });
    },
  });
};
// Get Income Entries for a specific sales receipt (partial payments)
// Note: no orderBy to avoid requiring a composite Firestore index; sorted client-side
export const usePaymentsByReceipt = (receiptId: string) => {
  const auth = getAuth();
  const userID = auth?.currentUser?.uid!;
  const colRef = collection(getDb(), `${basePath(userID)}/incomes`);

  return useQuery<Income[]>({
    queryKey: ["payments", receiptId],
    queryFn: async () => {
      const q = query(colRef, where("salesReceiptId", "==", receiptId));
      const snap = await getDocs(q);
      const results = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Income[];
      return results.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date((a.createdAt as any)?.seconds * 1000 || 0);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date((b.createdAt as any)?.seconds * 1000 || 0);
        return dateB.getTime() - dateA.getTime();
      });
    },
    enabled: !!receiptId,
  });
};

// ************************************************************************************************************
